import type { ComponentType, ReactNode, SVGProps } from 'react';

import { cn, COLORS } from '#/lib/utils';

import {
  AgentIcon,
  DecisionIcon,
  LlmIcon,
  McpIcon,
  TranscendMark,
  WebIcon,
} from './icons';

/** Resting colour for connectors and card borders before the pulse passes. */
export const LINE_COLOR = COLORS.grayscale['04'];

export const AGENT_LABELS = [
  'Agent apps',
  'Custom agents',
  'Provisioned agents',
];

export const ENDPOINTS: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  detail: string;
}[] = [
  { icon: LlmIcon, title: 'LLM providers', detail: 'OpenAI, Bedrock' },
  { icon: McpIcon, title: 'MCP servers', detail: 'Slack, Notion' },
  { icon: WebIcon, title: 'Web & APIs', detail: 'Wikipedia, Salesforce' },
];

type RectSize = { w: number; h: number; rx: number };

// Fixed card geometry (from the Figma frame). The border-draw percentages
// below are derived from these, so the components render at exactly these
// sizes rather than sizing to content.
// Wider than Figma's 180 so "Your Agents on Rails" / "Provisioned agents"
// stay on one line.
export const AGENTS_RECT: RectSize = { w: 216, h: 269, rx: 21 };
export const PLANE_RECT: RectSize = { w: 477, h: 231, rx: 12 };
export const CHIP_HEIGHT = 50;
export const DECISION_RECT: RectSize = { w: 212, h: 75, rx: 17 };
export const ENDPOINT_RECT: RectSize = { w: 218, h: 75, rx: 21 };
export const LOGO_SIZE = 74;

/**
 * Where each edge midpoint sits along a `CardBorder` path, as a % of the
 * perimeter. The path starts at the top-centre and runs clockwise, so these
 * are exact quarters. Feed them to `growFrom` as the entry point.
 */
export const EDGE = { top: 0, right: 25, bottom: 50, left: 75 } as const;

/**
 * Rounded-rect outline as a single path starting at the top-centre and
 * running clockwise. Drawn as a <path> (not a <rect>) so DrawSVG measures it
 * with getTotalLength() — it treats <rect>s as `2w + 2h`, ignoring the
 * rounded corners, which throws the percentages off by a few px.
 */
function roundedRectPath({ w, h, rx }: RectSize, inset: number) {
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  const arc = (x: number, y: number, sweep = 1) =>
    `A${rx} ${rx} 0 0 ${sweep} ${x} ${y}`;
  return [
    `M${w / 2} ${y0}`,
    `H${x1 - rx}`,
    arc(x1, y0 + rx),
    `V${y1 - rx}`,
    arc(x1 - rx, y1),
    `H${x0 + rx}`,
    arc(x0, y1 - rx),
    `V${y0 + rx}`,
    arc(x0 + rx, y0),
    'Z',
  ].join(' ');
}

/**
 * Resting outline plus a single DrawSVG path (`#id`) on top of it. The card
 * sizes are fixed, so no measuring is needed.
 */
function CardBorder({
  id,
  rect,
  litClassName,
}: {
  id: string;
  rect: RectSize;
  /** Extra class on the lit path, for timelines that need to target it. */
  litClassName?: string;
}) {
  const d = roundedRectPath(rect, 0.75);
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      width={rect.w}
      height={rect.h}
      viewBox={`0 0 ${rect.w} ${rect.h}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={d} stroke={LINE_COLOR} strokeWidth={1} />
      <path
        id={id}
        d={d}
        className={cn('stroke-electric-04 svg-draw', litClassName)}
        strokeWidth={1.5}
      />
    </svg>
  );
}

/**
 * Figma's "profile circle": a 32px hairline ring with the glyph centred in a
 * `size` box. Drawn inline so the ring width stays a true 0.65px.
 */
function IconHolder({
  size = 43,
  children,
}: {
  size?: number;
  children: ReactNode;
}) {
  return (
    <div
      className="relative flex shrink-0 items-center justify-center text-cloud"
      style={{ width: size, height: size }}
    >
      <svg
        className="absolute size-[32px]"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx={16}
          cy={16}
          r={15.67}
          stroke="currentColor"
          strokeWidth={0.65}
        />
      </svg>
      {children}
    </div>
  );
}

export function Connector({
  id,
  orientation,
  className,
}: {
  id: string;
  orientation: 'horizontal' | 'vertical';
  className?: string;
}) {
  const horizontal = orientation === 'horizontal';
  const d = horizontal ? 'M0 1H100' : 'M1 0V100';
  return (
    <svg
      viewBox={horizontal ? '0 0 100 2' : '0 0 2 100'}
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        horizontal ? 'h-[2px] shrink-0' : 'w-[2px] shrink-0',
        className,
      )}
    >
      <path d={d} stroke={LINE_COLOR} />
      <path
        d={d}
        id={id}
        className="stroke-electric-04 svg-draw"
        strokeWidth={2}
      />
    </svg>
  );
}

export function AgentsCard({
  borderId,
  activeButton,
}: {
  borderId: string;
  activeButton: number;
}) {
  return (
    <div
      className="relative flex shrink-0 flex-col items-center gap-16 rounded-[21px] bg-charcoal p-16"
      style={{ width: AGENTS_RECT.w, height: AGENTS_RECT.h }}
    >
      <CardBorder id={borderId} rect={AGENTS_RECT} />
      <div className="flex w-full flex-col items-center gap-8">
        <IconHolder size={49}>
          <AgentIcon className="relative size-[21px]" />
        </IconHolder>
        <p className="text-center font-azeret text-11 uppercase leading-113 tracking-[1.65px] text-grayscale-01">
          Your Agents on Rails
        </p>
      </div>
      {AGENT_LABELS.map((label, index) => (
        <div
          key={label}
          className={cn(
            'flex w-full items-center justify-center rounded-8 border px-12 py-8 text-16 leading-140 transition-colors duration-300',
            activeButton === index
              ? 'border-electric text-electric'
              : 'border-grayscale-05 text-cloud',
          )}
        >
          {label}
        </div>
      ))}
    </div>
  );
}

export function LogoNode({
  ringId,
  className,
}: {
  ringId: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center justify-center rounded-full bg-[#232325] shadow-[0_2px_10px_rgba(95,91,247,0.12)]',
        className,
      )}
      style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
    >
      <svg
        className="absolute inset-0 size-full"
        viewBox={`0 0 ${LOGO_SIZE} ${LOGO_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx={LOGO_SIZE / 2}
          cy={LOGO_SIZE / 2}
          r={LOGO_SIZE / 2 - 0.5}
          stroke={LINE_COLOR}
        />
        <circle
          id={ringId}
          cx={LOGO_SIZE / 2}
          cy={LOGO_SIZE / 2}
          r={LOGO_SIZE / 2 - 0.5}
          className="stroke-electric-04 svg-draw"
          strokeWidth={1.5}
        />
      </svg>
      <TranscendMark
        className="relative h-[42px] w-[42px] text-cloud"
        role="img"
        aria-label="Transcend"
      />
    </div>
  );
}

/**
 * Only the outer border animates (it lights up fully while a decision is
 * being made); the chips inside are static.
 */
export function ControlPlane({ borderId }: { borderId: string }) {
  return (
    <div
      className="relative flex shrink-0 flex-col items-center justify-center gap-24 rounded-12 p-32"
      style={{ width: PLANE_RECT.w, height: PLANE_RECT.h }}
    >
      <CardBorder id={borderId} rect={PLANE_RECT} />
      <p className="font-azeret text-11 uppercase leading-113 tracking-[1.65px] text-grayscale-01">
        Agent control plane
      </p>
      <div className="flex w-full gap-12">
        {['Inference', 'MCP', 'Network'].map((label) => (
          <div
            key={label}
            className="flex flex-1 items-center justify-center rounded-8 border border-grayscale-04 bg-charcoal px-8 text-center text-16 leading-113 text-grayscale-01"
            style={{ height: CHIP_HEIGHT }}
          >
            {label}
          </div>
        ))}
      </div>
      <p className="text-16 leading-113 text-cloud">Memory · Secrets</p>
    </div>
  );
}

/** Class the timelines use to pulse the whole card while a decision is made. */
export const DECISION_CARD_CLASS = 'rails-decision-card';
/** Class on the card's lit border, so a "no" decision can turn it red. */
export const DECISION_BORDER_CLASS = 'rails-decision-border';
/** No true red in the palette; flame is the closest "stop" colour. */
export const DECISION_NO_COLOR = COLORS.flame.DEFAULT;

export function DecisionApi({ borderId }: { borderId: string }) {
  return (
    <div
      className={cn(
        DECISION_CARD_CLASS,
        'relative flex shrink-0 items-center gap-8 rounded-[17px] bg-charcoal p-16',
      )}
      style={{ width: DECISION_RECT.w, height: DECISION_RECT.h }}
    >
      <CardBorder
        id={borderId}
        rect={DECISION_RECT}
        litClassName={DECISION_BORDER_CLASS}
      />
      <IconHolder>
        <DecisionIcon className="relative size-[21px]" />
      </IconHolder>
      <div className="flex flex-col gap-4">
        <p className="whitespace-nowrap text-16 leading-113 text-grayscale-01">
          Decision API
        </p>
        <p className="whitespace-nowrap text-12 leading-140 text-grayscale-03">
          Enforce + Audit
        </p>
      </div>
    </div>
  );
}

export function EndpointCard({
  borderId,
  icon: Icon,
  title,
  detail,
}: {
  borderId: string;
  icon: (typeof ENDPOINTS)[number]['icon'];
  title: string;
  detail: string;
}) {
  return (
    <div
      className="relative flex shrink-0 items-center gap-8 rounded-[21px] bg-charcoal p-16"
      style={{ width: ENDPOINT_RECT.w, height: ENDPOINT_RECT.h }}
    >
      <CardBorder id={borderId} rect={ENDPOINT_RECT} />
      <IconHolder>
        <Icon className="relative size-[24px]" />
      </IconHolder>
      <div className="flex min-w-0 flex-col gap-4">
        <p className="whitespace-nowrap text-16 leading-140 text-cloud">
          {title}
        </p>
        <p className="whitespace-nowrap text-12 leading-140 text-grayscale-03">
          {detail}
        </p>
      </div>
    </div>
  );
}
